// Typed wrappers for the codebase analysis endpoints (apps/api/src/routes/codebase-analysis.ts). Refusals are
// `policy_denied` with `details.cause`: start 403 (disabled | not_attested | no_repository | no_jev_connection) or 503
// (engine unavailable); answers 409 (analysis_running | no_machine_verdict) or 403 (project_halted); reopen 409
// (analysis_running). The attestation is written by an organization admin on a signed-in session only.
import { api } from "./client.ts";
import type {
  AnalysisAnswerRequest,
  AnalysisAnswerResult,
  AnalysisReopenResult,
  Attestation,
  AttestRequest,
  CodebaseAnalysisDetail,
  CodebaseAnalysisRun,
  FromGithubRequest,
  FromGithubResult,
  RepositoryProvision,
  StartAnalysisResult,
} from "./types-onboarding.ts";

const enc = encodeURIComponent;
/** The attestation text version the card shows (contracts ATTESTATION_TEXT_VERSION, checked by the type). */
const TEXT_VERSION: AttestRequest["text_version"] = "codebase-analysis-attestation-1";

export const startCodebaseAnalysis = (projectId: string, prdVersionId: string) =>
  api<StartAnalysisResult>(`/projects/${enc(projectId)}/codebase-analyses`, { method: "POST", body: { prd_version_id: prdVersionId } });

/** The project's latest 20 runs of one PRD version, newest first. */
export const listCodebaseAnalyses = (projectId: string, prdVersionId: string, signal?: AbortSignal) =>
  api<{ items: CodebaseAnalysisRun[] }>(`/projects/${enc(projectId)}/codebase-analyses`, { query: { prd_version_id: prdVersionId }, ...(signal ? { signal } : {}) });

export const getCodebaseAnalysis = (runId: string, signal?: AbortSignal) => api<CodebaseAnalysisDetail>(`/codebase-analyses/${enc(runId)}`, signal ? { signal } : {});

export const cancelCodebaseAnalysis = (runId: string) => api<CodebaseAnalysisRun>(`/codebase-analyses/${enc(runId)}/cancel`, { method: "POST", body: {} });

export const answerCodebaseAnalysis = (runId: string, body: AnalysisAnswerRequest) => api<AnalysisAnswerResult>(`/codebase-analyses/${enc(runId)}/answers`, { method: "POST", body });

/** Bulk undo (C4): reopens the run's closures still done/existing and untouched since. */
export const reopenCodebaseAnalysis = (runId: string) => api<AnalysisReopenResult>(`/codebase-analyses/${enc(runId)}/reopen`, { method: "POST", body: {} });

export const getAttestation = (signal?: AbortSignal) => api<Attestation>("/codebase-analysis/attestation", signal ? { signal } : {});

export const attest = () => api<Attestation>("/codebase-analysis/attestation", { method: "POST", body: { accept: true, text_version: TEXT_VERSION } });

export const revokeAttestation = () => api<Attestation>("/codebase-analysis/attestation", { method: "DELETE" });

// GitHub onboarding (apps/api/src/routes/repository-provision.ts), organization admin: 422 `validation_failed` (field
// `repository`) when the connection cannot reach the repository or it is not the project's; 503 when the engine is
// unavailable (the provision is then `failed`/`connection_unavailable`).

/** Creates the project, its mapping and provision; one Idempotency-Key per opened dialog (a retry replays the project). */
export const createProjectFromGithub = (body: FromGithubRequest, idempotencyKey: string) =>
  api<FromGithubResult>("/projects/from-github", { method: "POST", body, idempotencyKey });

/** Connects an existing project, or retries a failed provision: 202 new, 200 with the provision still in flight. */
export const provisionRepository = (projectId: string, body: FromGithubRequest) =>
  api<{ provision: RepositoryProvision }>(`/projects/${enc(projectId)}/repository/provision`, { method: "POST", body });

/** The project's latest provision, or null when it was never connected from GitHub. */
export const getProvision = (projectId: string, signal?: AbortSignal) =>
  api<{ provision: RepositoryProvision | null }>(`/projects/${enc(projectId)}/repository/provision`, signal ? { signal } : {});
